<script setup>
import { useAuthStore } from "@stores/auth";
import { watch } from "vue";
import moment from "moment-timezone";
import { useSettingsStore } from "@stores/settings";

const settingsStore = useSettingsStore();
const authStore = useAuthStore();
const runtimeConfig = useRuntimeConfig();
const route = useRoute();

const settings = settingsStore.settings;
const DIGITAIN_SERVER = runtimeConfig.public.DIGITAIN_SERVER;
const DIGITAIN_SPORT_PARTNER = runtimeConfig.public.DIGITAIN_SPORT_PARTNER;

const token = ref("");
const isLoading = ref(false);
const emit = defineEmits(["isLoading"])

// Adicionar variáveis para armazenar referências
const messageEventListener = ref(null);
const phraseInterval = ref(null);

function openSport(sportId) {
	SportFrame.openEvent({
		sportId: parseInt(sportId),
		eventStatus: "prematch",
	});
}

onMounted(async () => {
	if (!settings.cometa_sports_book) {
		return navigateTo("/");
	}

	if (settings.sport_enabled) {
		await createdIframe();
	}

	// Armazenar referência do event listener
	messageEventListener.value = function(event) {
		const message = event.data.args?.data?.message;
		if (!message) return;

		if (message?.pathname === "/event-details" && message?.qs?.event) {
			window.history.replaceState(
				{},
				"title",
				`/sports/EventView/${message.qs.event}`,
			);
		}
	};

	window.addEventListener("message", messageEventListener.value);

	window.onDigitainContentLoaded = () => {
		if (
			route.params.page?.[0] === "Home" &&
			/^\d+$/.test(route.params.page?.[1])
		) {
			setTimeout(() => {
				openSport(route.params.page?.[1]);
			}, 1000);
		}

		setTimeout(() => {
			isLoading.value = false;
			emit("isLoading", isLoading.value);
		}, 1500);
	};

	window.setMobileCustomCss = () => {
		const customCssUrl = settings.cometa_sportsbook_css;
		const styleElement = window.document.createElement("link");
		styleElement.rel = "stylesheet";
		styleElement.href = customCssUrl;

		const betSlipStyleElement = window.document.createElement("link");
		betSlipStyleElement.rel = "stylesheet";
		betSlipStyleElement.href = customCssUrl;

		if (window.sportdigi && window.sportdigi.mobile?.target) {
			window.sportdigi.mobile.target.appendChild(styleElement);
		}

		const betSlipRootInner = document.getElementById("betslip-root-inner");
		if (
			betSlipRootInner &&
			betSlipRootInner.firstChild?.contentWindow?.document?.head
		) {
			betSlipRootInner.firstChild.contentWindow.document.head.appendChild(
				betSlipStyleElement,
			);
		} else {
			console.warn("betslip-root-inner or its firstChild is not found");
		}
	};
});

onUnmounted(async () => {
	// Limpar event listener
	if (messageEventListener.value) {
		window.removeEventListener("message", messageEventListener.value);
		messageEventListener.value = null;
	}

	// Limpar intervalos
	if (phraseInterval.value) {
		clearInterval(phraseInterval.value);
		phraseInterval.value = null;
	}

	// Limpar callbacks globais
	window.onDigitainContentLoaded = null;
	window.setMobileCustomCss = null;

	// Limpar elementos DOM
	const elementsToRemove = [
		"betslip-container-element",
		"sport",
		"script",
		"sp_frame_boot",
		"sp_frame_loader"
	];

	elementsToRemove.forEach(id => {
		const element = document.getElementById(id);
		if (element) {
			element.remove();
		}
	});

	// Limpar referências
	token.value = "";
	isLoading.value = false;
});

async function createdIframe() {
	isLoading.value = true;
	if (useCookie("jwt").value) {
		await getToken();
	}
	const script = document.createElement("script");
	script.src = `${DIGITAIN_SERVER}/js/partner/bootstrapper.min.js`;
	script.type = "text/javascript";
	script.setAttribute("id", "sp_frame_boot");
	document.body.appendChild(script);

	if (isMobileDevice()) {
		script.addEventListener("load", initIFrameMobile);
	} else {
		const scriptIntegration = document.createElement("script");
		scriptIntegration.src = `${DIGITAIN_SERVER}/js/partner/IntegrationLoader.min.js`;
		scriptIntegration.type = "text/javascript";
		scriptIntegration.setAttribute("id", "sp_frame_loader");
		scriptIntegration.addEventListener("load", initIFrameDesktop);
		document.body.appendChild(scriptIntegration);
	}
}

function isMobileDevice() {
	return window.innerWidth <= 768;
}

function convertTimeZoneStringToGMTOffset() {
	const timeZone = moment().tz(settings.general_time_zone).format("ZZ");

	return Number(timeZone.slice(0, 3));
}

const getToken = async () => {
	token.value = await authStore.getSportBookToken();
};

const balanceChangeHandler = (data) => {
	console.log(data);
};

const login = async () => {
	authStore.modalOpen("login");
};

watch(
	() => authStore.isLoggedIn,
	(newValue, oldValue) => {
		if (newValue) {
			const divToRemove = document.getElementById("sport_div_iframe");
			divToRemove.remove();

			removeBootScripts();

			setTimeout(() => {
				const novaDiv = document.createElement("div");
				novaDiv.id = "sport_div_iframe";
				const divSport = document.getElementById("sport");
				divSport.appendChild(novaDiv);

				createdIframe();
			}, 1000);
		}
	},
);

const removeBootScripts = () => {
	const bootstrapper = document.getElementById("sp_frame_boot");
	const frameLoader = document.getElementById("sp_frame_loader");
	bootstrapper?.parentNode?.removeChild?.(bootstrapper);
	frameLoader?.parentNode?.removeChild?.(frameLoader);
};

const registration = () => {
	console.log("registration");
};

const pageParamIdType = {
	Upcoming: "champId",
	UpcomingDetails: "eventId",
	EventView: "eventId",
};

function initIFrameDesktop() {
	var _sp = _sp || [];
	let page = route.params.page?.[0];
	let paramId = route.params.page?.[1] ?? "1";

	_sp.push(["server", DIGITAIN_SERVER]);

	_sp.push(["sportPartner", DIGITAIN_SPORT_PARTNER]);

	_sp.push(["token", token.value]);

	_sp.push(["language", "br"]);

	_sp.push(["currentPage", page]);

	_sp.push(["device", "d"]);

	_sp.push(["fixedHeight", true]);

	_sp.push(["timeZone", `${convertTimeZoneStringToGMTOffset()}`]);

	_sp.push(["userId", useCookie("user_id").value]);

	if (pageParamIdType[page]) {
		_sp.push([pageParamIdType[page], paramId]);
	}

	_sp.push(["isSuperTip", route.query?.isSuperTip]);

	_sp.push(["balanceChangeCallback", balanceChangeHandler]);

	_sp.push(["onContentLoaded", "onDigitainContentLoaded"]);

	_sp.push(["login", login]);

	_sp.push(["registration", registration]);

	_sp.push(["unHideOverflow", true]);

	_sp.push([
		"onNavigateCallback",
		(data) => {
			if (data.pageName && data.targetId) {
				window.history.replaceState(
					{},
					"title",
					`/sports/${data.pageName}/${data.targetId}`,
				);
			}
		},
	]);

	_sp.push([
		"eventsHandler",
		(msg) => {
			if (msg.eventType === "navigateToSport" && msg.data.id) {
				window.history.replaceState({}, "title", `/sports/Home/${msg.data.id}`);
			}
		},
	]);

	window.addEventListener("message", function (event) {
		const message = event.data.args?.data?.message;

		if (!message) return;

		if (message?.pathname === "/event-details" && message?.qs?.event) {
			window.history.replaceState(
				{},
				"title",
				`/sports/EventView/${message.qs.event}`,
			);
		}
	});

	_sp.push([
		"customCssUrl",
		encodeURIComponent(settings.cometa_sportsbook_css),
	]);

	SportFrame.frame(_sp);
}

function mobileNavigateTo(route = "/", data = {}) {
	const routeList = {
		Home: "/pre-match", // Used to expand specific Sport menu [id(optional)]
		Overview: "/live", // Used to open Live page
		Upcoming: "/SportEvents", // Used to open league page [id(required)]
		EventView: "/events", // Used to open event page [id(required)]
	};

	if (route === "Home") {
		data.subRoute = `?q={"${data.subRoute}":{}}`;
	} else {
		data.subRoute = data.subRoute ? `/${data.subRoute}` : "";
	}

	route = routeList[route] ?? route;

	return this.navigateTo(`${route}${data.subRoute}`);
}

function replaceMobileUrl(data) {
	const locationPath = String(data.pathname);
	const splittedPath = locationPath.split("/").slice(1);

	if (splittedPath[0] === "/event-details") {
		return;
	}

	let queryParams = null;

	switch (splittedPath[0]) {
		case "pre-match":
			splittedPath[0] = "/Home";
			queryParams = decodeURIComponent(data.search).replace("?q=", "");
			break;
		case "live":
			splittedPath[0] = "/Overview";
			queryParams = decodeURIComponent(data.search).replace("?q=", "");
			break;
		case "SportEvents":
			splittedPath[0] = "/Upcoming";
			break;
	}

	if (queryParams) {
		const sportId = Object.keys(JSON.parse(queryParams))[0];

		if (/^\d+$/.test(sportId)) {
			splittedPath.push(sportId);
		}
	}

	const finalRoute = splittedPath.join("/");

	window.history.replaceState({}, "title", `/sports${finalRoute}`);
}

async function initIFrameMobile() {
	var params = {
		server: DIGITAIN_SERVER,

		containerId: "sport_div_iframe",

		token: token.value,

		defaultLanguage: "br",

		timeZone: convertTimeZoneStringToGMTOffset(),

		hashRouterDisabled: true,

		unHideOverflow: true,

		bottomNavBar: false,

		theme: "dark",

		loginTrigger: function () {
			login();
		},

		onBalanceChange: function () {
			//
		},

		onUniqueIdChange: (uuid) => {
			console.log({ uuid });
		},

		// {Object} Various events callbacks.
		events: {
			onAppMount: function appMountCallback() {
				setMobileCustomCss();
				onDigitainContentLoaded();
			},
			onAppUnmount: function appUnmountCallback() {
				// Do something on app unmount
			},
		},

		sportId: 1,

		externalLinksOpenInside: true,

		oddsFormatList: [0, 1, 2],

		oddsFormat: 0,

		sportPartner: DIGITAIN_SPORT_PARTNER,
	};

	let mobile = await Bootstrapper.boot(params, { name: "Mobile" });

	mobile.addEventListener("onNavigate", function (event) {
		replaceMobileUrl(event.data.location);
	});
	mobile.addEventListener("navigateToEventPage", function (event) {
		window.history.replaceState(
			{},
			"title",
			`/sports/EventView/${event.data.targetId}`,
		);
	});

	const routeParams = route.params.page;

	mobile.to = mobileNavigateTo;

	mobile.to(routeParams[0], {
		subRoute: routeParams[1],
		query: route.query,
	});
}

const phrases = [
	"Carregando esportes",
	"Aguarde um momento",
	"Quase lá",
	"Carregando...",
	"Buscando as melhores odds",
];
const currentPhrase = ref("Carregando esportes");

const updatePhrase = () => {
	const randomIndex = Math.floor(Math.random() * phrases.length);
	currentPhrase.value = phrases[randomIndex];
};

onMounted(() => {
	phraseInterval.value = setInterval(updatePhrase, 2500);
});
</script>
<template>
  <div id="sport_div_iframe" class="relative w-full overflow-hidden md:rounded-sm" />
</template>

<style>
.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>